import { useMemo, useState } from "react";
import { GenericField, InputProps } from "../Fields/GenericField";
import { CheckIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SelectOption } from "@/lib/Components/Form/Components/Select";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronsUpDown } from "lucide-react";
import { useField } from "formik";
import { Input } from "@headlessui/react";
import { Button } from "@/components/catalyst/button";
import { useTranslations } from "use-intl";

export type OptionGroup<T = any> = {
  label: string;
  options: SelectOption<T>[];
};

export type SelectInputProps<T = any> = InputProps & {
  onChange?: (value: T | null) => void;
  clearable?: boolean;
  options: OptionGroup<T>[];
  initialOpen?: boolean;
  disabled?: boolean;
  filter?: (value: string, search: string) => number;
  placeholder?: string;
  popoverWidth?: string;
};

export function SelectInput<T extends string>({
  options,
  onChange,
  initialOpen = false,
  disabled,
  filter,
  clearable,
  placeholder,
  popoverWidth,
  ...props
}: SelectInputProps<T>) {
  const t = useTranslations("common.form");
  const [open, setOpen] = useState(initialOpen);
  const finalPlaceholder = placeholder ?? t("select_placeholder");
  const [{ value }, meta, { setValue }] = useField(props.name);
  const showError = !!meta.error && meta.touched;

  const selected = useMemo(
    () =>
      options
        .flatMap((group) => group.options)
        .find((option) => option.value === value),
    [options, value],
  );

  return (
    <GenericField viewNode={<span>{selected?.label ?? "--"}</span>} {...props}>
      {() => (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Input
              as={Button}
              {...{ plain: true }}
              disabled={disabled}
              invalid={showError}
              role="combobox"
              type="button"
              aria-expanded={open}
              className={cn(
                "shadow-xs h-[36px] w-full min-w-0 bg-white text-sm font-normal dark:bg-zinc-900",
                showError
                  ? "border-red-500 hover:border-red-500"
                  : "border-gray-300",
              )}
            >
              <span
                className={cn(
                  // Basic layout
                  "w-full",
                  // Disabled state
                  "disabled:border-zinc-950/20",

                  // Layout for content
                  "flex items-center justify-between gap-2",
                )}
              >
                <span
                  className={cn("grow truncate text-left", {
                    "text-gray-400": !value,
                  })}
                >
                  {selected?.label ?? finalPlaceholder}
                </span>

                {clearable && value ? (
                  <XMarkIcon
                    data-slot="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setValue(null);
                    }}
                    className="size-3.5 shrink-0 text-zinc-500 dark:text-zinc-400"
                  />
                ) : (
                  <ChevronsUpDown
                    className="size-4 shrink-0 text-zinc-500 dark:text-zinc-400"
                    data-slot="icon"
                  />
                )}
              </span>
            </Input>
          </PopoverTrigger>
          {open ? (
            <PopoverContent className={cn("p-0", popoverWidth)} align="start">
              <Command filter={filter}>
                <CommandInput placeholder="Search..." className="h-9" />
                <CommandList>
                  <CommandEmpty>No item found.</CommandEmpty>
                  <div className="">
                    {options.map((group, index) => {
                      if (group.options.length === 0) {
                        return null;
                      }

                      return (
                        <CommandGroup heading={group.label} key={index}>
                          {group.options.map((option) => (
                            <CommandItem
                              key={option.value}
                              value={option.value}
                              keywords={
                                option.description
                                  ? [
                                      String(option.label),
                                      String(option.description),
                                    ]
                                  : [String(option.label)]
                              }
                              onSelect={async () => {
                                await setValue(option.value);
                                onChange?.(option.value);
                                setOpen(false);
                              }}
                            >
                              <div className="flex flex-col">
                                <span>{option.label}</span>
                                {option.description ? (
                                  <span className="text-xs text-gray-500">
                                    {option.description}
                                  </span>
                                ) : null}
                              </div>
                              <CheckIcon
                                className={cn(
                                  "ml-auto h-4 w-4",
                                  value === option.value
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      );
                    })}
                  </div>
                </CommandList>
              </Command>
            </PopoverContent>
          ) : null}
        </Popover>
      )}
    </GenericField>
  );
}
